import {
  Checkbox,
  Fab,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material'
import { Icons, SkeletonText } from 'utoniq-ui'

import {
  ConsoleOfficialInformationConnectionFragment,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { Head } from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { ScrollFetcher } from '@/components/organisms/functionals'
import {
  OfficialInformationConsoleBox,
  OfficialInformationConsoleBoxSkeleton,
} from '@/components/organisms/notification'
import { ArtistThemeProvider, styles } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  artistId: string
  loading: boolean
  hasNextPage: boolean
  isFilterByPublished: boolean
  officialInformations: Maybe<ConsoleOfficialInformationConnectionFragment>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFilterByPublished: () => void
  onFetchMore: () => Promise<void>
  onDelete: (officialInformationId: string) => Promise<void>
}

export const ConsoleOfficialInformationsTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
  onFilterByPublished,
  onDelete,
}) => {
  const { t } = useTranslation('consoleOfficialInformations')
  const { routerNames } = useRouter()

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsoleOfficialInformations} />
      <ConsoleBaseLayout
        margin
        header={{
          back: true,
          title: t('title'),
        }}
      >
        <HeaderCell
          loading={state.loading}
          /**
           * @description
           * Open when function is implemented in the future
           * https://app.clickup.com/t/908127/UT-17780
           */
          hiddenFilter={
            true
            // !state.loading && !state?.officialInformations?.edges?.length
          }
          isFilterByPublished={state.isFilterByPublished}
          onFilterByPublished={onFilterByPublished}
        />
        <Stack py={3}>
          <ScrollFetcher
            nodes={state.officialInformations?.edges.map(({ node }) => (
              <OfficialInformationConsoleBox
                officialInformation={node}
                onDelete={onDelete}
              />
            ))}
            componentProps={{ spacing: 6 }}
            loading={state.loading}
            hasNextPage={state.hasNextPage}
            onFetchMore={onFetchMore}
            emptyMessage={t('officialInformationEmptyMessage')}
            loadingComponent={
              <Stack spacing={6} sx={{ py: 2 }}>
                {Array.from(Array(5).keys()).map(key => (
                  <OfficialInformationConsoleBoxSkeleton key={key} />
                ))}
              </Stack>
            }
          />
        </Stack>
        <BaseCell />
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

type HeaderCellProps = {
  loading: State['loading']
  hiddenFilter: boolean
  isFilterByPublished: State['isFilterByPublished']
  onFilterByPublished: Action['onFilterByPublished']
}
const HeaderCell: Cell<HeaderCellProps> = ({
  loading,
  hiddenFilter,
  isFilterByPublished,
  onFilterByPublished,
}) => {
  const { t } = useTranslation('consoleOfficialInformations')

  if (loading) {
    return (
      <CellFrame sx={{ pt: 2 }}>
        <Stack py={3}>
          <SkeletonText variant="h3" width="40%" />
        </Stack>
      </CellFrame>
    )
  }

  return (
    <CellFrame sx={{ pt: 2 }}>
      <Typography variant="h3" py={3}>
        {t('title')}
      </Typography>
      {!hiddenFilter && (
        <Stack>
          <FormControlLabel
            control={
              <Checkbox
                checked={isFilterByPublished}
                onClick={onFilterByPublished}
              />
            }
            label={
              <Typography variant="caption" color="text.secondary">
                {t('checkBoxLabel', '公開予定のみ表示')}
              </Typography>
            }
          />
        </Stack>
      )}
    </CellFrame>
  )
}

const BaseCell: Cell = () => {
  const { routerPages } = useRouter()
  return (
    <Stack direction="row-reverse">
      <Stack
        sx={{
          position: 'fixed',
          bottom: 16,
          zIndex: 5,
          maxWidth: 600,
          margin: '0 auto',
          px: 4,
          pb: styles.safeAreaBottom,
        }}
      >
        <Fab
          size="medium"
          color="primary"
          href={routerPages.ConsoleOfficialInformationCreate}
        >
          <Icons.ThinPlus fontSize="medium" sx={{ color: 'white' }} />
        </Fab>
      </Stack>
    </Stack>
  )
}
